import {
    BarChart3,
    Building,
    Building2,
    ChevronRight,
    Coins,
    CreditCard,
    Globe,
    Home,
    LogOut,
    Menu,
    Moon,
    Sun,
    Users,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/use-auth';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Separator } from '../ui/separator';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { AssetTabs } from './asset-tabs';
import { BrandSwitcher } from './brand-switcher';
import { BrandTabs } from './brand-tabs';
import { OrganizationBreadcrumb } from './organization-breadcrumb';
import { useTheme } from './use-theme';

interface NavItem {
    title: string;
    href: string;
    icon: React.ElementType;
    disabled?: boolean;
    comingSoon?: boolean;
}

const navigation: NavItem[] = [
    { title: 'Dashboard', href: '/', icon: Home },
    { title: 'Brands', href: '/brands', icon: Building2 },
    { title: 'Organizations', href: '/organizations', icon: Building },
    { title: 'Domains', href: '/domains', icon: Globe },
    { title: 'AI Usage', href: '/ai-usage', icon: BarChart3 },
    { title: 'Rate Cards', href: '/rate-cards', icon: Coins },
    { title: 'Payment Plans', href: '/payment-plans', icon: CreditCard },
    { title: 'Users', href: '/users', icon: Users, disabled: true, comingSoon: true },
];

interface AppLayoutProps {
    children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
    const { user, logout } = useAuth();
    const location = useLocation();
    const { theme, setTheme } = useTheme();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = () => {
        logout().catch(() => {
            // Error handling is done in the logout function
        });
    };

    const toggleTheme = () => {
        setTheme(theme === 'dark' ? 'light' : 'dark');
    };

    const isActive = (path: string) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    const SidebarContent = () => (
        <div className="flex h-full flex-col">
            <div className="p-6">
                <h2 className="text-2xl font-bold">Slate Admin</h2>
            </div>

            <Separator />

            <nav className="flex-1 space-y-1 p-4">
                <TooltipProvider>
                    {navigation.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.href);

                        if (item.disabled) {
                            const navItem = (
                                <div
                                    key={item.href}
                                    className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground opacity-50 cursor-not-allowed"
                                >
                                    <Icon className="h-5 w-5" />
                                    {item.title}
                                </div>
                            );

                            if (item.comingSoon) {
                                return (
                                    <Tooltip key={item.href}>
                                        <TooltipTrigger asChild>{navItem}</TooltipTrigger>
                                        <TooltipContent>
                                            <p>Coming soon</p>
                                        </TooltipContent>
                                    </Tooltip>
                                );
                            }

                            return navItem;
                        }

                        return (
                            <Link
                                key={item.href}
                                to={item.href}
                                onClick={() => {
                                    setSidebarOpen(false);
                                }}
                                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                                    active
                                        ? 'bg-primary text-primary-foreground'
                                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                }`}
                            >
                                <Icon className="h-5 w-5" />
                                {item.title}
                            </Link>
                        );
                    })}
                </TooltipProvider>
            </nav>
        </div>
    );

    const pathSegments = location.pathname.split('/').filter(Boolean);

    const breadcrumbs =
        pathSegments.length === 0
            ? [{ label: 'Dashboard', path: '/', isBrandIdentifier: false, isOrganizationIdentifier: false }]
            : pathSegments.map((segment, index) => {
                  const isBrandIdentifier = index === 1 && pathSegments[0] === 'brands';
                  const isOrganizationIdentifier = index === 1 && pathSegments[0] === 'organizations';

                  return {
                      label: segment.charAt(0).toUpperCase() + segment.slice(1),
                      path: `/${pathSegments.slice(0, index + 1).join('/')}`,
                      isBrandIdentifier,
                      isOrganizationIdentifier,
                  };
              });

    const isInBrandContext = pathSegments[0] === 'brands' && pathSegments.length >= 2;

    const assetTypes = ['collections', 'stickers', 'colors', 'fonts'];
    const isOnAssetPage =
        isInBrandContext && pathSegments.length >= 3 && pathSegments[2] && assetTypes.includes(pathSegments[2]);

    return (
        <div className="flex h-screen overflow-hidden">
            <aside className="hidden w-64 border-r bg-card lg:block">
                <SidebarContent />
            </aside>

            <div className="flex flex-1 flex-col overflow-hidden">
                <header className="border-b bg-card">
                    <div className="flex h-16 items-center justify-between px-6">
                        <div className="flex items-center gap-3">
                            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                                <SheetTrigger asChild className="lg:hidden">
                                    <Button variant="ghost" size="icon" className="shrink-0">
                                        <Menu className="h-6 w-6" />
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="left" className="w-64 p-0">
                                    <SidebarContent />
                                </SheetContent>
                            </Sheet>

                            <div className="flex items-center gap-2 text-sm">
                                {breadcrumbs.map((crumb, index) => (
                                    <div key={crumb.path} className="flex items-center gap-2">
                                        {index > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                        {crumb.isBrandIdentifier ? (
                                            <BrandSwitcher />
                                        ) : crumb.isOrganizationIdentifier ? (
                                            <OrganizationBreadcrumb />
                                        ) : index === breadcrumbs.length - 1 ? (
                                            <span className="font-medium">{crumb.label}</span>
                                        ) : (
                                            <Link
                                                to={crumb.path}
                                                className="text-muted-foreground hover:text-foreground"
                                            >
                                                {crumb.label}
                                            </Link>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={toggleTheme}>
                                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                            </Button>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="gap-2">
                                        <Avatar className="h-8 w-8">
                                            <AvatarFallback>
                                                {user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="hidden md:inline-block">{user?.email}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </header>

                {isInBrandContext && <BrandTabs />}

                {isOnAssetPage && <AssetTabs />}

                <main className="flex-1 overflow-auto bg-background p-6">{children}</main>
            </div>
        </div>
    );
}
